// @ts-nocheck

import { reduce as _reduce } from 'lodash';

import { BasketModes, BasketStatuses, BasketItemStatuses } from '../../../types/basket';
import { basket as BasketUtils } from '../../../utils';
import { getStakeFromPresets } from '../../../utils/presets';
import recomputeBasketStateForOperations from '../utils/recomputeBasketStateForOperations';

const { formatStake, checkIfItemCanBeAdded, getMode, generateSystems } = BasketUtils;

/**
 * Return the nextState corresponding to the BASKET_ADD_ITEM action
 *
 * @static
 * @param  {Object} state            The actual state
 * @param  {Object} action           The action to reduce
 * @param  {Object} options.entities The entities's state
 * @return {Object}                  Returns the new state.
 */

const addItem = (state, action, globalState) => {
  const { entities, user, history } = globalState;
  const { mode, status, items, stake, defaultStake, freebets, selectedFreebetId, isCashout } = state;

  let nextStake = stake || defaultStake;
  const { oddId, betId, matchId, isFromLineUp, ts, isFromMultichoice, isRepeatBet, isFromHistory } = action.payload;
  const { matches, bets, odds } = entities;
  const userPresets = user?.presets;
  let match = matches[matchId];
  let bet = bets[betId];
  const value = odds[oddId];

  if (isFromHistory) {
    match = history.entities.matches[matchId];
    bet = history.entities.bets[betId];
  }

  if (checkIfItemCanBeAdded({ match, bet })) {
    // NOTE: if basket is validated we have to start from a cleaned object items
    let nextItems = {};

    if (status !== BasketStatuses.VALIDATED && status !== BasketStatuses.WAITING) {
      nextItems = _reduce(
        items,
        (finalItems, item, key) => {
          if (
            item.status !== BasketItemStatuses.VALIDATED &&
            item.status !== BasketItemStatuses.WAITING &&
            item.status !== BasketItemStatuses.REJECTED
          ) {
            return {
              ...finalItems,
              [key]: {
                ...item,
                status: BasketItemStatuses.OK,
                betslipId: undefined,
                error: undefined,
                additionalMessage: undefined,
              },
            };
          }
          return finalItems;
        },
        {},
      );
    }

    const newItemStake = stake || getStakeFromPresets(userPresets, value) || defaultStake;

    // add item
    nextItems[oddId] = {
      status: BasketItemStatuses.OK,
      oddId,
      matchId,
      betId,
      value,
      sportId: match.sportId,
      banker: false,
      stake: formatStake(newItemStake),
      sortBy: ts,
      isFromLineUp,
      isFromMultichoice,
      isRepeatBet,
      isFromHistory,
    };

    // recompute the mode
    const nextMode = getMode(mode, nextItems, { matches });
    // FIXME: Is there a case were we pass through this block?
    if (nextMode === BasketModes.COMBINED) {
      if (selectedFreebetId) {
        const freebet = freebets[selectedFreebetId];

        if (freebet !== undefined) {
          const { amount } = freebet;
          nextStake = amount / 100;
        } else {
          nextStake = defaultStake;
        }
      }
    }

    if (!isCashout && userPresets) {
      let odds = 1;

      if (nextMode === BasketModes.SIMPLE) {
        odds = value;
      } else if (nextMode === BasketModes.COMBINED) {
        odds = _reduce(nextItems, (acc, { value, validatedValue }) => acc * (validatedValue || value), 1);
      }

      nextStake = stake || getStakeFromPresets(userPresets, odds) || defaultStake;
    }

    let nextSystems = undefined;
    if (nextMode === BasketModes.SYSTEM) {
      nextSystems = generateSystems({
        items: nextItems,
        defaultCheckboxValue: mode !== nextMode,
      });
    }

    return {
      ...state,
      status: BasketStatuses.OK,
      mode: nextMode,
      items: nextItems,
      systems: nextSystems,
      stake: formatStake(nextStake),
      itemsMemoizedForSelection: {},
      error: undefined,
      additionalMessage: undefined,
      betslipId: undefined,
      isFromLineUp: Object.keys(nextItems).some((key) => nextItems[key].isFromLineUp),
    };
  }

  return state;
};

const basketAddItem = (basketState, action, globalState) => {
  const { status } = basketState;

  let nextState = basketState;

  if (status !== BasketStatuses.PENDING) {
    nextState = addItem(nextState, action, globalState);
    nextState = recomputeBasketStateForOperations(nextState, globalState);
  }

  return nextState;
};

export default basketAddItem;
